import React, { useState } from 'react'
import { Shield, Sparkles, AlertCircle, CheckCircle2, Zap } from 'lucide-react'
import { api } from '../lib/api'
import { toast } from '../store/toastStore'
import clsx from 'clsx'

interface StreakShieldProps {
  available: boolean
  usedThisMonth: boolean
  streakDays: number
  onUsed: () => void
}

export function StreakShield({ available, usedThisMonth, streakDays, onUsed }: StreakShieldProps) {
  const [loading, setLoading] = useState(false)
  
  // A streak is "at risk" or "broken" if it's 0 but they have an available shield.
  // We'll also show it as a benefit if their streak is > 0.
  const isBroken = streakDays === 0 && available

  async function handleUseShield() {
    setLoading(true)
    try {
      const res = await api.useStreakShield()
      if (res.used) {
        toast.success("Streak Shield activated! Your momentum is preserved. 🕊️")
        onUsed()
      } else {
        toast.error(res.reason || "Could not use shield")
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to use shield")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div 
      className={clsx(
        "rounded-2xl border p-5 transition-all duration-300 shadow-sm hover:shadow-md",
        isBroken ? "bg-amber-50 border-amber-200 animate-pulse-subtle" : "bg-warm-card border-warm-border"
      )}
    >
      <div className="flex items-start gap-4">
        <div 
          className={clsx(
            "w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-inner",
            isBroken ? "bg-amber-100 text-amber-700" : usedThisMonth ? "bg-emerald-50 text-emerald-600" : "bg-brand-sage/20 text-brand-teal"
          )}
        >
          {usedThisMonth ? <CheckCircle2 size={24} /> : <Shield size={24} className={clsx(isBroken && "animate-bounce-slow")} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-ink text-sm flex items-center gap-1.5">
              Streak Shield
              {available && !isBroken && (
                <span className="text-[10px] bg-brand-teal/10 text-brand-teal px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                  Active
                </span>
              )}
            </h3>
            <span className="text-[10px] text-ink-soft font-mono uppercase tracking-widest">Monthly Grace</span>
          </div>

          <div className="mt-1.5">
            {isBroken ? (
              <>
                <p className="text-xs text-amber-900 font-medium leading-relaxed">
                  Oh no! Your streak reset today. Use your monthly grace day to repair it instantly!
                </p>
                <button
                  onClick={handleUseShield}
                  disabled={loading}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition-all shadow-glow-amber active:scale-95 disabled:opacity-50"
                >
                  {loading ? "Repairing..." : (
                    <>
                      <Zap size={14} className="fill-current" />
                      Repair My Streak
                    </>
                  )}
                </button>
              </>
            ) : usedThisMonth ? (
              <p className="text-xs text-emerald-800 leading-relaxed">
                You used your shield this month! Your streak is protected from a 1-day gap. Restores next month.
              </p>
            ) : (
              <p className="text-xs text-ink-muted leading-relaxed">
                Your habit is protected. If you miss a day, you can use this shield to keep your streak alive.
              </p>
            )}
          </div>
        </div>
      </div>
      
      {!isBroken && !usedThisMonth && (
        <div className="mt-4 pt-4 border-t border-warm-border/60 flex items-center gap-2">
          <Sparkles size={12} className="text-brand-teal" />
          <p className="text-[10px] text-ink-faint">
            One-time repair available until the end of {new Date().toLocaleString('default', { month: 'long' })}.
          </p>
        </div>
      )}
    </div>
  )
}
